import React, { useEffect, useState, useRef, createElement } from 'react';
interface VideoPlayerProps {
  mediaId?: string;
}
export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  mediaId = 'vic2dhkti8'
}) => {
  const playerScriptRef = useRef<HTMLScriptElement | null>(null);
  const embedScriptRef = useRef<HTMLScriptElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    const loadScripts = async () => {
      try {
        // Load player script
        if (!playerScriptRef.current) {
          const playerScript = document.createElement('script');
          playerScript.src = 'https://fast.wistia.com/player.js';
          playerScript.async = true;
          playerScript.crossOrigin = 'anonymous';
          playerScript.onerror = () => {
            console.error('Failed to load Wistia player script');
            setHasError(true);
          };
          playerScriptRef.current = playerScript;
          document.head.appendChild(playerScript);
          // Wait for player script to load
          await new Promise((resolve, reject) => {
            playerScript.onload = resolve;
            playerScript.onerror = reject;
          });
        }
        // Load embed script
        if (!embedScriptRef.current) {
          const embedScript = document.createElement('script');
          embedScript.src = `https://fast.wistia.com/embed/${mediaId}.js`;
          embedScript.async = true;
          embedScript.crossOrigin = 'anonymous';
          embedScript.type = 'module';
          embedScript.onerror = () => {
            console.error('Failed to load Wistia embed script');
            setHasError(true);
          };
          embedScriptRef.current = embedScript;
          document.head.appendChild(embedScript);
          // Wait for embed script to load
          await new Promise((resolve, reject) => {
            embedScript.onload = resolve;
            embedScript.onerror = reject;
          });
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading Wistia scripts:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };
    loadScripts();
    return () => {
      if (playerScriptRef.current && document.head.contains(playerScriptRef.current)) {
        document.head.removeChild(playerScriptRef.current);
      }
      if (embedScriptRef.current && document.head.contains(embedScriptRef.current)) {
        document.head.removeChild(embedScriptRef.current);
      }
    };
  }, [mediaId]);
  if (hasError) {
    return <div className="relative w-full max-w-5xl mx-auto rounded-lg overflow-hidden bg-gray-900 aspect-video flex items-center justify-center" data-id="element-93">
        <p className="text-white text-center px-4" data-id="element-94">
          Unable to load video player. Please refresh the page or try again
          later.
        </p>
      </div>;
  }
  return <div className="relative w-full max-w-5xl mx-auto rounded-lg overflow-hidden" data-id="element-95">
      <style data-id="element-96">
        {`
          wistia-player[media-id='${mediaId}']:not(:defined) {
            background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/${mediaId}/swatch');
            display: block;
            filter: blur(5px);
            padding-top: 56.25%;
          }
          @media (max-width: 640px) {
            wistia-player {
              border-radius: 0.5rem;
            }
          }
        `}
      </style>
      <wistia-player media-id={mediaId} aspect="1.7777777777777777" className="rounded-lg w-full" data-id="element-97" />
      {isLoading && <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center" data-id="element-98">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-4 border-white border-t-transparent" data-id="element-99" />
        </div>}
    </div>;
};